import React, { useEffect, useState } from 'react';
import { adminFetch } from '../../lib/api';
import { GitBranch, ChevronRight, Euro, GripVertical } from 'lucide-react';

const STAGES = [
  { key: 'neu', label: 'Neu', color: '#8b95a5', emoji: '🆕' },
  { key: 'kontaktiert', label: 'Kontaktiert', color: '#3b82f6', emoji: '📞' },
  { key: 'interessiert', label: 'Interessiert', color: '#f59e0b', emoji: '⭐' },
  { key: 'demo', label: 'Demo', color: '#8b5cf6', emoji: '📺' },
  { key: 'verhandlung', label: 'Verhandlung', color: '#ec4899', emoji: '🤝' },
  { key: 'gewonnen', label: 'Gewonnen', color: '#10b981', emoji: '🏆' },
  { key: 'verloren', label: 'Verloren', color: '#ef4444', emoji: '❌' },
];

const PRIORITY_COLORS: Record<string, string> = {
  low: '#6b7280', medium: '#3b82f6', high: '#f59e0b', urgent: '#ef4444',
};

export const SalesPipelinePage: React.FC = () => {
  const [pipeline, setPipeline] = useState<Record<string, any[]>>({});
  const [loading, setLoading] = useState(true);
  const [dragItem, setDragItem] = useState<string | null>(null);

  useEffect(() => { loadPipeline(); }, []);

  const loadPipeline = async () => {
    try {
      const res = await adminFetch('/api/admin/sales/pipeline');
      const data = await res.json();
      setPipeline(data && typeof data === 'object' && !Array.isArray(data) ? data : {});
    } catch (error) {
      console.error(error);
      setPipeline({});
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = async (leadId: string, newStatus: string) => {
    try {
      await adminFetch(`/api/admin/sales/pipeline/${leadId}/move`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newStatus }),
      });
      loadPipeline();
    } catch (error) {
      console.error(error);
    }
    setDragItem(null);
  };

  const totalByStage = (stage: string) => {
    const leads = pipeline[stage] || [];
    return {
      count: leads.length,
      value: leads.reduce((s: number, l: any) => s + (l.estimated_value || 0), 0),
    };
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header"><h1>Pipeline</h1></div>
        <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 16 }}>
          {STAGES.map(s => <div key={s.key} className="card skeleton" style={{ minWidth: 260, height: 300 }} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><GitBranch style={{ marginRight: 8, verticalAlign: 'middle' }} size={24} /> Pipeline</h1>
      </div>

      <div style={{
        display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 16,
        minHeight: 'calc(100vh - 200px)',
      }}>
        {STAGES.map(stage => {
          const stats = totalByStage(stage.key);
          const leads = pipeline[stage.key] || [];
          return (
            <div
              key={stage.key}
              onDragOver={e => e.preventDefault()}
              onDrop={() => { if (dragItem) handleDrop(dragItem, stage.key); }}
              style={{
                minWidth: 260, maxWidth: 300, flex: '0 0 260px',
                display: 'flex', flexDirection: 'column',
                background: 'var(--bg-secondary)', borderRadius: 12, padding: 12,
                borderTop: `3px solid ${stage.color}`,
              }}
            >
              {/* Column Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>{stage.emoji}</span>
                  <strong style={{ fontSize: 14 }}>{stage.label}</strong>
                  <span style={{
                    background: stage.color, color: '#fff', borderRadius: 10,
                    padding: '1px 8px', fontSize: 11, fontWeight: 700,
                  }}>{stats.count}</span>
                </div>
                {stats.value > 0 && (
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    {(stats.value / 100).toLocaleString('de-DE')} €
                  </span>
                )}
              </div>

              {/* Cards */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto' }}>
                {leads.length === 0 ? (
                  <div style={{
                    padding: 24, textAlign: 'center', color: 'var(--text-secondary)',
                    fontSize: 12, border: '2px dashed var(--border)', borderRadius: 8,
                  }}>
                    Lead hierher ziehen
                  </div>
                ) : (
                  leads.map((lead: any) => (
                    <div
                      key={lead.id}
                      draggable
                      onDragStart={() => setDragItem(lead.id)}
                      onDragEnd={() => setDragItem(null)}
                      style={{
                        background: 'var(--bg-primary)', borderRadius: 10, padding: '12px 14px',
                        cursor: 'grab', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        borderLeft: `3px solid ${PRIORITY_COLORS[lead.priority] || '#666'}`,
                        opacity: dragItem === lead.id ? 0.5 : 1,
                        transition: 'opacity 0.2s, box-shadow 0.2s',
                      }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)'; }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <strong style={{ fontSize: 13, lineHeight: 1.3 }}>{lead.name}</strong>
                        <GripVertical size={14} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                        📍 {lead.city}
                      </div>
                      {lead.contact_person && (
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                          👤 {lead.contact_person}
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                        {lead.estimated_value > 0 && (
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#10b981' }}>
                            {(lead.estimated_value / 100).toLocaleString('de-DE')} €
                          </span>
                        )}
                        {lead.next_action && (
                          <span style={{ fontSize: 11, color: 'var(--text-secondary)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            ➡️ {lead.next_action}
                          </span>
                        )}
                      </div>
                      {lead.tags?.length > 0 && (
                        <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                          {lead.tags.map((tag: string, i: number) => (
                            <span key={i} style={{
                              fontSize: 10, padding: '1px 6px', borderRadius: 6,
                              background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
                            }}>{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
